import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  GlassModal,
  GlassModalContent,
  GlassButton,
  SectionHeader,
  GlassColors,
} from '../../components/glassmorphism/GlassmorphismStyles';
import BrowseHandymanList from './BrowseHandymanList';
import React, { useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { HandymanAppState } from '../../configuration/redux/reducers';
import GoogleMapsWrapper from '../UserProfile/components/UserDetails/GoogleMapsWrapper';
import { getAuthData } from '../../helpers/localStorageHelpers';
import { HandymanViewModel } from '../../configuration/api/apiSchema';
import BrowseHandymanSkeleton from './skeletons/BrowseHandymanSkeleton';

interface StateFromProps {
  isLoading: boolean;
}
type SelectedHandyman = {
  id: string;
  name: string;
  categories: string[];
  city?: string;
  cityDistrict?: string;
};

const BrowseHandymanPage = (props: StateFromProps) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [authData, setAuthData] = useState(() => getAuthData());
  const [showAuthModal, setShowAuthModal] = useState(false);
  const isAuthed = !!authData;

  useEffect(() => {
    const sync = () => setAuthData(getAuthData());
    window.addEventListener('focus', sync);
    window.addEventListener('storage', sync);

    return () => {
      window.removeEventListener('focus', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const userDetails = useSelector((s: HandymanAppState) => s.user.userDetails);

const isNotHandyman = authData?.role !== 'handyman';

  let isLoggedIn = !!authData;
  const shouldVerify =
    isLoggedIn && !isNotHandyman &&
    ((userDetails?.kycStatus !== 'Verified' &&
      userDetails?.kycStatus !== undefined) ||
      (authData?.role === 'handyman' &&
        userDetails?.isStripeAccountVerified === false));

  const handleCreateOrderClick = React.useCallback(
    (handyman?: SelectedHandyman, selectedCategoryKeys?: string[]) => {
      if (shouldVerify) {
        navigate('/verification');
      }
      if (isAuthed) {
        // User is authenticated, navigate to create order page
        navigate('/create-order', {
          state: {
            handyman,
            selectedCategoryKeys,
          },
        });
      } else {
        // navigate to auth
        navigate('/sign-in', { state: { from: 'browse-handyman' } });
      }
    },
    [isAuthed, navigate],
  );

  const handleVerifyClick = React.useCallback(() => {
    navigate('/verification');
  }, [navigate]);

  const handleSignUp = () => {
    setShowAuthModal(false);
    navigate('/sign-up');
  };

  const handleSignIn = () => {
    setShowAuthModal(false);
    navigate('/sign-in');
  };

  const handleCloseModal = () => {
    setShowAuthModal(false);
  };
  const onAction = shouldVerify ? handleVerifyClick : handleCreateOrderClick;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '22px',
        gap: { xs: '24px', sm: '48px' },
        minHeight: '100vh',
        background: isDark ? '#1A1A1A' : '#E5E5DC',
        paddingTop: { xs: '100px', tablet: '136px' },
      }}
    >
      {/* Page Title */}
      <Typography
        component='h2'
        sx={{
          fontFamily: 'Montserrat, sans-serif',
          fontWeight: { xs: 600, sm: 500 },
          fontSize: { xs: '34px', sm: '60px' },
          color: isDark ? GlassColors.accent : GlassColors.secondary,
          lineHeight: { xs: '124%', sm: '120%' },
          letterSpacing: { xs: '0.25px', sm: '-.5px' },
          marginBottom: 0,
          textAlign: 'center',
        }}
      >
        Browse Handyman
      </Typography>

      {/* Content */}
      <Box sx={{ width: '100%', maxWidth: '1152px' }}>
        <GoogleMapsWrapper fallback={<BrowseHandymanSkeleton />}>
          <BrowseHandymanList
            isNotHandyman={isNotHandyman}
            actionButtonTitle={
              isAuthed
                ? shouldVerify
                  ? 'Verify to propose task'
                  : 'Propose task'
                : 'Sign in to propose task'
            }
            onAction={handleCreateOrderClick}
          />
        </GoogleMapsWrapper>
      </Box>

      {/* Authentication Modal unused*/}
      <GlassModal open={showAuthModal} onClose={handleCloseModal}>
        <GlassModalContent>
          <Box textAlign='center' p={3}>
            <SectionHeader sx={{ mb: 2 }}>Sign In Required</SectionHeader>
            <Typography
              sx={{ mb: 3, color: isDark ? '#D5D5D5' : 'text.secondary' }}
            >
              You should sign up/sign in before creating an order
            </Typography>
            <Box display='flex' justifyContent='center' gap={2}>
              <GlassButton variant='outlined' onClick={handleSignIn}>
                Sign In
              </GlassButton>
              <GlassButton onClick={handleSignUp}>Sign Up</GlassButton>
            </Box>
          </Box>
        </GlassModalContent>
      </GlassModal>
    </Box>
  );
};

const mapStateToProps = (state: HandymanAppState): StateFromProps => ({
  isLoading: state.handyman.isLoading,
});

export default connect<StateFromProps, {}, {}, HandymanAppState>(
  mapStateToProps,
  {},
)(BrowseHandymanPage);
